import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Header from '../Components/cleaning/Header.jsx';
import ZipCodeValidator, { VALID_CHICAGO_ZIP_CODES } from '../Components/cleaning/ZipCodeValidator';
import ServiceSelector from '../Components/cleaning/ServiceSelector.jsx';
import SizeSlider from '../Components/cleaning/SizeSlider.jsx';
import RoomBathCounters from '../Components/cleaning/RoomBathCounters.jsx';
import ExtraServices from '../Components/cleaning/ExtraServices.jsx';
import DatePicker from '../Components/cleaning/DatePicker.jsx';
import EntryInfo from '../Components/cleaning/EntryInfo.jsx';
import RequestsComments from '../Components/cleaning/RequestsComments.jsx';
import Payment from '../Components/cleaning/Payment.jsx';
import CustomerInfo from '../Components/cleaning/CustomerInfo.jsx';
import BookingConfirmationCard from '../Components/cleaning/BookingConfirmationCard.jsx';

const BOOKING_STORAGE_KEY = 'fresh-shine-booking-draft';
const BOOKING_SUMMARY_STORAGE_KEY = 'fresh-shine-last-booking';

const loadStoredBooking = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(BOOKING_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const readLooseStorageValue = (key) => {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(key);
    if (value === 'null' || value === 'undefined') return null;
    return value;
  } catch {
    return null;
  }
};

const readSessionValue = (key) => {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.sessionStorage.getItem(key);
    if (value === 'null' || value === 'undefined') return null;
    return value;
  } catch {
    return null;
  }
};

const loadStoredSummary = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(BOOKING_SUMMARY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const persistSummary = (summary) => {
  if (typeof window === 'undefined') return;
  try {
    if (!summary) {
      window.localStorage.removeItem(BOOKING_SUMMARY_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(BOOKING_SUMMARY_STORAGE_KEY, JSON.stringify(summary));
  } catch {
    // ignore storage errors
  }
};

const clearBookingIdentifiers = () => {
  if (typeof window === 'undefined') return;
  try {
    ['bookingId', 'customerId', 'customerEmail', 'calBookingUid', 'bookingUid'].forEach((key) =>
      window.localStorage.removeItem(key)
    );
    ['bookingId', 'calBookingUid', 'bookingUid', 'calSelection'].forEach((key) =>
      window.sessionStorage.removeItem(key)
    );
  } catch {
    // ignore storage errors
  }
};

const persistBooking = (snapshot) => {
  if (typeof window === 'undefined') return;
  try {
    const { bookingId, calBookingUid, bookingUid, ...rest } = snapshot || {};
    window.localStorage.setItem(BOOKING_STORAGE_KEY, JSON.stringify(rest));
  } catch {
    // Ignore storage errors (private mode, quota exceeded, etc.)
  }
};

export default function CleaningService() {
  const storedBooking = useMemo(() => loadStoredBooking(), []);
  const storedCustomerDetails = storedBooking?.customerDetails || {};
  const storedZipFromBooking = storedBooking?.zipCode || '';
  const initialLooseEmail = readLooseStorageValue('customerEmail') || '';
  const initialCustomerEmail =
    storedCustomerDetails.email || storedBooking?.customerEmail || initialLooseEmail || '';

  const smoothScrollTo = useCallback((targetY) => {
    if (
      typeof window === 'undefined' ||
      typeof requestAnimationFrame === 'undefined' ||
      typeof performance === 'undefined'
    )
      return;
    const startY = window.scrollY || window.pageYOffset || 0;
    const distance = targetY - startY;
    const duration = 1200;
    const startTime = performance.now();
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const step = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);
      window.scrollTo(0, startY + distance * easedProgress);
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }, []);

  const entrySectionRef = useRef(null);
  const calSectionRef = useRef(null);
  const customerSectionRef = useRef(null);
  const customerInfoCardRef = useRef(null);
  const paymentSectionRef = useRef(null);

  const scrollCustomerInfoIntoView = useCallback(() => {
    if (!customerInfoCardRef.current || typeof window === 'undefined') return;
    const { top } = customerInfoCardRef.current.getBoundingClientRect();
    const offset = Math.max(top + window.scrollY - 290, 0);
    smoothScrollTo(offset);
  }, [smoothScrollTo]);

  const [validZip, setValidZip] = useState(storedBooking?.validZip || false);
  const [zipCode, setZipCode] = useState(storedZipFromBooking);
  const [selectedService, setSelectedService] = useState(storedBooking?.selectedService || 'basic');
  const [size, setSize] = useState(
    typeof storedBooking?.size === 'number' && storedBooking.size > 0 ? storedBooking.size : 1000
  );
  const [rooms, setRooms] = useState(storedBooking?.rooms || 'Studio');
  const [baths, setBaths] = useState(storedBooking?.baths || '1');
  const [selectedExtras, setSelectedExtras] = useState(storedBooking?.selectedExtras || []);
  const [entryInfo, setEntryInfo] = useState(storedBooking?.entryInfo || {});
  const [requests, setRequests] = useState(storedBooking?.requests || '');
  const [customerDetails, setCustomerDetails] = useState(() => ({
    ...storedCustomerDetails,
    email: initialCustomerEmail
  }));
  const [customerZipManuallyEdited, setCustomerZipManuallyEdited] = useState(() => {
    const storedCustomerZip = storedCustomerDetails?.zip;
    if (!storedZipFromBooking || !storedCustomerZip) return false;
    return storedCustomerZip !== storedZipFromBooking;
  });
  const sessionBookingId = readSessionValue('bookingId');
  const sessionCalUid = readSessionValue('calBookingUid');
  const [calBooked, setCalBooked] = useState(
    storedBooking?.calBooked || (sessionBookingId && sessionCalUid)
  );
  const [bookingId, setBookingId] = useState(() => storedBooking?.bookingId || sessionBookingId || readLooseStorageValue('bookingId'));
  const [customerId, setCustomerId] = useState(() => storedBooking?.customerId || readLooseStorageValue('customerId'));
  const [customerEmail, setCustomerEmail] = useState(() => initialCustomerEmail);
  const [calBookingUid, setCalBookingUid] = useState(() => storedBooking?.calBookingUid || sessionCalUid || readLooseStorageValue('calBookingUid'));
  const [bookingSummary, setBookingSummary] = useState(() => loadStoredSummary());
  const [highlightSections, setHighlightSections] = useState([]);
  const [total, setTotal] = useState(0);
  const hasScrolledToSummaryRef = useRef(Boolean(bookingSummary));

  useEffect(() => {
    if (!zipCode || zipCode.length !== 5 || customerZipManuallyEdited) return;
    setCustomerDetails((prev) => {
      const prevDetails = prev || {};
      if (prevDetails.zip === zipCode) return prevDetails;
      return {
        ...prevDetails,
        zip: zipCode
      };
    });
  }, [zipCode, customerZipManuallyEdited]);

  useEffect(() => {
    const BASIC_RATE = 0.08;
    const DEEP_RATE = 0.15;
    const MIN_BASIC = 100;
    const MIN_DEEP = 180;

    const BASIC_SIZE_POINTS = [
      { size: 500, factor: 2.5 },
      { size: 900, factor: 2.1 },
      { size: 1200, factor: 1.9 },
      { size: 2000, factor: 1.55 },
      { size: 3000, factor: 1.3 },
      { size: 5000, factor: 1.2 }
    ];

    const DEEP_SIZE_POINTS = [
      { size: 500, factor: 2.4 },
      { size: 900, factor: 2.05 },
      { size: 1200, factor: 1.85 },
      { size: 2000, factor: 1.6 },
      { size: 3000, factor: 1.45 },
      { size: 5000, factor: 1.35 }
    ];

    const interpolateFactor = (sizeSqFt, points) => {
      const numericSize = Math.max(Number(sizeSqFt) || 0, 0);
      if (!points.length) return 1;
      if (numericSize <= points[0].size) return points[0].factor;

      for (let i = 1; i < points.length; i += 1) {
        const prev = points[i - 1];
        const curr = points[i];
        if (numericSize <= curr.size) {
          const ratio = (numericSize - prev.size) / (curr.size - prev.size);
          return prev.factor + ratio * (curr.factor - prev.factor);
        }
      }

      return points[points.length - 1].factor;
    };

    const sizeFactor = (sizeSqFt, serviceType) => {
      const points = serviceType === 'deep' ? DEEP_SIZE_POINTS : BASIC_SIZE_POINTS;
      return interpolateFactor(sizeSqFt, points);
    };

    const extraPrices = {
      oven: 35,
      fridge: 35,
      baseboards: 25,
      petHair: 30,
      dishwashing: 25,
      windows: 40,
      cabinets: 30,
      laundry: 25
    };

    const ratePerSqFt = selectedService === 'deep' ? DEEP_RATE : BASIC_RATE;
    const factor = sizeFactor(size, selectedService);
    const basePrice = size * ratePerSqFt * factor;

    const extrasTotal = selectedExtras.reduce((sum, extraId) => {
      return sum + extraPrices[extraId];
    }, 0);

    const subtotal = basePrice + extrasTotal;
    const minimum = selectedService === 'deep' ? MIN_DEEP : MIN_BASIC;
    const finalTotal = Math.max(subtotal, minimum);
    setTotal(finalTotal);
  }, [selectedService, size, rooms, baths, selectedExtras]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const persistField = (key, value) => {
        if (value) {
          window.localStorage.setItem(key, value);
        } else {
          window.localStorage.removeItem(key);
        }
      };

      window.localStorage.setItem('customerDetails', JSON.stringify(customerDetails || {}));
      persistField('customerEmail', customerDetails?.email);
      persistField('customerFirstName', customerDetails?.firstName);
      persistField('customerLastName', customerDetails?.lastName);
      persistField('customerPhone', customerDetails?.phone);
      persistField('customerAddress', customerDetails?.street);
      persistField('customerCity', customerDetails?.city);
      persistField('customerZip', customerDetails?.zip);
    } catch {
      // ignore storage errors
    }
  }, [customerDetails]);

  useEffect(() => {
    persistBooking({
      zipCode,
      validZip,
      selectedService,
      size,
      rooms,
      baths,
      selectedExtras,
      entryInfo,
      requests,
      customerDetails,
      calBooked,
      bookingId,
      customerId,
      customerEmail,
      calBookingUid
    });
  }, [
    zipCode,
    validZip,
    selectedService,
    size,
    rooms,
    baths,
    selectedExtras,
    entryInfo,
    requests,
    customerDetails,
    calBooked,
    bookingId,
    customerId,
    customerEmail,
    calBookingUid
  ]);

  useEffect(() => {
    persistSummary(bookingSummary);
  }, [bookingSummary]);

  useEffect(() => {
    const hasSummary = Boolean(bookingSummary);
    if (hasSummary && !hasScrolledToSummaryRef.current && calSectionRef.current) {
      calSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    hasScrolledToSummaryRef.current = hasSummary;
  }, [bookingSummary, calSectionRef]);

  const handleCalComplete = (details = {}) => {
    setCalBooked(true);
    if (details.bookingId) {
      setBookingId(details.bookingId);
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem('bookingId', details.bookingId);
          window.sessionStorage.setItem('bookingUid', details.bookingId);
        } catch {
          // ignore storage errors
        }
      }
    }
    if (details.customerId) {
      setCustomerId(details.customerId);
    }
    if (details.customerEmail) {
      setCustomerEmail(details.customerEmail);
      setCustomerDetails((prev) => ({ ...prev, email: details.customerEmail }));
    }
    if (details.bookingUid || details.calBookingUid) {
      const uid = details.bookingUid || details.calBookingUid;
      setCalBookingUid(uid);
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem('calBookingUid', uid);
        } catch {
          // ignore storage errors
        }
      }
    }
    if (details.summary) {
      setBookingSummary(details.summary);
    }
  };

  const handleCalendarSelectionChange = (selection) => {
    if (selection) return;
    setCalBooked(false);
    setBookingId(null);
    setCalBookingUid(null);
    setBookingSummary(null);
    clearBookingIdentifiers();
  };

  const handleRestartBooking = () => {
    setCalBooked(false);
    setBookingSummary(null);
    setBookingId(null);
    setCustomerId(null);
    setCustomerEmail('');
    setCalBookingUid(null);
    setCustomerZipManuallyEdited(false);
    clearBookingIdentifiers();
  };

  const handleMissingRequirements = (sections = []) => {
    const order = ['customer', 'entry', 'cal', 'terms'];
    const normalizedNext = Array.isArray(sections)
      ? order.filter((key) => sections.includes(key))
      : [];
    setHighlightSections((prev) => {
      if (
        prev.length === normalizedNext.length &&
        prev.every((value, index) => value === normalizedNext[index])
      ) {
        return prev;
      }
      return normalizedNext;
    });
  };

  const requiresEntryNotes = useMemo(() => ['hiding_spot', 'lockbox'], []);
  const isValidEmail = (value) => /\S+@\S+\.\S+/.test(String(value || '').trim());
  const isValidPhone = (value) =>
    /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(String(value || '').trim());

  const handleRequirementFocus = (section) => {
    if (section === 'customer') {
      scrollCustomerInfoIntoView();
    } else {
      const refs = {
        entry: entrySectionRef,
        cal: calSectionRef,
        terms: paymentSectionRef
      };
      const targetRef = refs[section];
      if (targetRef?.current) {
        targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    setHighlightSections((prev) => {
      if (prev.includes(section)) return prev;
      return [...prev, section];
    });
  };

  const handleCustomerDataChange = (nextDetails) => {
    setCustomerDetails(nextDetails);
    if (nextDetails?.email !== undefined && nextDetails.email !== customerEmail) {
      setCustomerEmail(nextDetails.email || '');
    }
  };

  const handleCustomerZipEdit = () => {
    setCustomerZipManuallyEdited(true);
  };

  const handleCustomerZipComplete = (completedZip) => {
    setCustomerZipManuallyEdited(true);
    setZipCode((prevZip) => (prevZip === completedZip ? prevZip : completedZip));
    setValidZip(VALID_CHICAGO_ZIP_CODES.includes(completedZip));
  };

  const needsEntrySelectionHighlight = highlightSections.includes('entry') && !entryInfo.type;
  const needsEntryDetailsHighlight =
    highlightSections.includes('entry') &&
    entryInfo.type &&
    requiresEntryNotes.includes(entryInfo.type) &&
    !entryInfo.notes?.trim();

  const isEntryInfoComplete = useMemo(() => {
    if (!entryInfo?.type) return false;
    if (requiresEntryNotes.includes(entryInfo.type)) {
      return Boolean(entryInfo.notes && entryInfo.notes.trim());
    }
    return true;
  }, [entryInfo, requiresEntryNotes]);

  const isCustomerInfoComplete = useMemo(() => {
    const { firstName, lastName, email, phone, street, city, zip } = customerDetails || {};
    const hasNames = Boolean(firstName?.trim() && lastName?.trim());
    const hasAddress = Boolean(street?.trim() && city?.trim() && String(zip || '').trim().length === 5);
    const hasContact = isValidEmail(email) && isValidPhone(phone);
    return hasNames && hasAddress && hasContact;
  }, [customerDetails]);

  const missingCustomerFields = useMemo(() => {
    const missing = [];
    const { firstName, lastName, email, phone, street, city, zip } = customerDetails || {};
    if (!firstName?.trim()) missing.push('firstName');
    if (!lastName?.trim()) missing.push('lastName');
    if (!street?.trim()) missing.push('street');
    if (!city?.trim()) missing.push('city');
    if (!String(zip || '').trim() || String(zip || '').trim().length < 5) missing.push('zip');
    if (!isValidEmail(email)) missing.push('email');
    if (!isValidPhone(phone)) missing.push('phone');
    return missing;
  }, [customerDetails]);

  const needsCustomerHighlight =
    highlightSections.includes('customer') && !isCustomerInfoComplete;

  const handleEntryGuardClick = () => {
    handleRequirementFocus('entry');
  };

  return (
    <div
      className="min-h-screen"
    >
      <Header total={total} />

      <main className="page-shell">
        {/* Hero section removed per request */}

        <section className="section-stack">
          <ZipCodeValidator
            zipCode={zipCode}
            validZip={validZip}
            onZipChange={setZipCode}
            onValidChange={setValidZip}
          />

          {zipCode.length === 5 && (
            <>
              <ServiceSelector selectedService={selectedService} onServiceChange={setSelectedService} />

              <div className="card section-panel panel-cream">
                <SizeSlider size={size} onSizeChange={setSize} />
              </div>

              <div className="card section-panel panel-cream">
                <RoomBathCounters
                  rooms={rooms}
                  baths={baths}
                  onRoomsChange={setRooms}
                  onBathsChange={setBaths}
                />
              </div>

              <div className="card section-panel panel-cream">
                <ExtraServices selectedExtras={selectedExtras} onExtrasChange={setSelectedExtras} />
              </div>

              <div
                ref={entrySectionRef}
                className={
                  'card section-panel panel-cream' + (needsEntrySelectionHighlight ? ' highlight-required' : '')
                }
              >
                <EntryInfo
                  entryInfo={entryInfo}
                  onEntryInfoChange={setEntryInfo}
                  highlightDetails={needsEntryDetailsHighlight}
                />
              </div>

              <div className="card section-panel panel-cream">
                <RequestsComments requests={requests} onRequestsChange={setRequests} />
              </div>

              {!isEntryInfoComplete && (
                <div className="card section-panel panel-cream calendar-guard-card">
                  <div className="calendar-guard">
                    <p className="calendar-guard__title">Almost there—complete these steps:</p>
                    <ul className="calendar-guard__list">
                      <li>
                        <button
                          type="button"
                          className="calendar-guard__link"
                          onClick={handleEntryGuardClick}
                        >
                          Add entry details or instructions
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              <section
                ref={calSectionRef}
                className={
                  'card section-panel panel-cream' +
                  (validZip ? '' : ' muted-panel') +
                  (highlightSections.includes('cal') ? ' highlight-required' : '')
                }
              >
                {bookingSummary ? (
                  <BookingConfirmationCard
                    summary={bookingSummary}
                    onRestartBooking={handleRestartBooking}
                  />
                ) : (
                  <DatePicker
                    service={selectedService}
                    size={size}
                    rooms={rooms}
                    baths={baths}
                    extras={selectedExtras}
                    total={total}
                    entryInfo={entryInfo}
                    requests={requests}
                    onBookingComplete={handleCalComplete}
                    onSelectionChange={handleCalendarSelectionChange}
                    highlightMissing={highlightSections.includes('cal')}
                  />
                )}
              </section>

              <section
                ref={customerSectionRef}
                className={
                  'card section-panel panel-cream' +
                  (validZip ? '' : ' muted-panel') +
                  (needsCustomerHighlight ? ' highlight-required' : '')
                }
              >
                <CustomerInfo
                  cardRef={customerInfoCardRef}
                  customerData={customerDetails}
                  onCustomerDataChange={handleCustomerDataChange}
                  onZipEdit={handleCustomerZipEdit}
                  onZipComplete={handleCustomerZipComplete}
                  highlightMissing={needsCustomerHighlight}
                  missingFields={missingCustomerFields}
                />
              </section>

              <section
                ref={paymentSectionRef}
                className={'card section-panel panel-cream' + (validZip ? '' : ' muted-panel')}
              >
                <Payment
                  total={total}
                  entryInfo={entryInfo}
                  rooms={rooms}
                  baths={baths}
                  size={size}
                  selectedExtras={selectedExtras}
                  selectedService={selectedService}
                  requests={requests}
                  validZip={validZip}
                  customerInfoComplete={isCustomerInfoComplete}
                  calBooked={calBooked}
                  bookingId={bookingId}
                  customerId={customerId}
                  customerEmail={customerEmail}
                  calBookingUid={calBookingUid}
                  customerDetails={customerDetails}
                  onMissingRequirements={handleMissingRequirements}
                  onRequirementFocus={handleRequirementFocus}
                  highlightTerms={highlightSections.includes('terms')}
                />
              </section>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
